<template>
    <evp-layout>
        <navigation-header
            :return-url="evp.isEmbeddedMode ? route('booking-institutions') : route('home')"
            :title="$t('global.HelpTitle')"
        />

        <div class="hidden lg:block h-[245px] mb-2 mt-[-55px] bg-no-repeat bg-contain bg-center bg-[url(@/images/help/helpdesk_title.png)]"></div>
        <div class="flex flex-wrap -mx-1">
            <template v-for="(category, index) in categories" :key="index">

                <div 
                    v-if="category.articles.length"
                    class="w-full md:w-1/2 mt-4 px-1"
                    :class="{
                        'animate-pulse-in-out ring ring-primary-dark/75 ring-3 rounded-lg py-1': highlightCategory && category.articles.some(
                            article => article.ArticleUri === activeArticleSlug
                        ),
                    }"
                    style="animation-iteration-count: 3;"
                >
                    <h2 class="text-lg text-neutral-600 pl-11 mb-2">
                        {{ category['CategoryTitle' + langCapitalized as keyof HelpCategory] }}:
                    </h2>
                    <div class="space-y-1">
                        <a
                            v-for="(article, articleIndex) in category['articles']"
                            :key="articleIndex"
                            :data-article-slug="article['ArticleUri']"
                            :class="{
                                'group button-transparent h-full bg-surface active:scale-[.98] hover:!bg-surface border border-transparent hover:border-primary-dark/75 hover:!text-primary-dark active:!text-white': true,
                                'animate-pulse-in-out ring ring-primary-dark/75 ring-3': !highlightCategory && article.ArticleUri === activeArticleSlug,
                            }"
                            :href="route('help-article', {'name': article['ArticleUri']})"
                            style="animation-iteration-count: 3;"
                        >
                            <div class="py-2.5 pl-3.5 pr-2 flex items-start justify-start h-full w-full bg-gradient-to-br hover:from-primary-light/10 hover:to-primary-light/30 group-active:bg-transparent transition-all">
                                {{ article['Title' + langCapitalized as keyof HelpArticle] }}
                            </div>
                        </a>
                    </div>
                </div>
            </template>
        </div>


        <h2 class="text-lg text-neutral-600 pl-11 mt-12 mb-2">{{ $t('help.HelpAgreementsHeader') }}:</h2>

        <div class="space-y-1">
            <a class="group button-transparent h-full bg-surface border border-transparent hover:!bg-surface hover:border-primary-dark/75 hover:!text-primary-dark active:!text-white" :href="route('user-agreement')">
                <div class="py-2.5 pl-3.5 pr-2 flex items-center justify-start h-full w-full bg-gradient-to-br hover:from-primary-light/10 hover:to-primary-light/30 group-active:bg-transparent transition-all">
                    <div class="flex items-center justify-center">
                        <svg 
                            class="h-7 w-7 mr-2"
                            xmlns:xlink="http://www.w3.org/1999/xlink"
                            version="1.1"
                            baseProfile="full"
                            viewBox="-336 338 76 76"
                            xml:space="preserve"
                        >
                            <path
                                d="M-278.8,367.9c-0.8-0.8-1.7-1.4-2.7-1.8c1.8-1.2,2.9-3.2,2.9-5.5c0-3.7-3-6.7-6.7-6.7c-3.7,0-6.7,3-6.7,6.7
                                c0,2.2,1.1,4.2,2.8,5.4c-2.3,0.9-4.2,2.5-5.5,4.7c-0.4,0.7-1.1,1.1-1.8,1.1h-3.2c-0.8,0-1.5-0.4-1.8-1.1c-1.3-2.2-3.2-3.8-5.5-4.7
                                c1.7-1.2,2.8-3.2,2.8-5.4c0-3.7-3-6.7-6.7-6.7s-6.7,3-6.7,6.7c0,2.3,1.2,4.3,2.9,5.5c-3.1,1.4-5.3,4.6-5.3,8.3v7.4
                                c0,1.5,1,2.8,2.4,3.2v10.5c0,1.4,1.2,2.6,2.6,2.6h8.1c1.4,0,2.6-1.2,2.6-2.6v-18.7c1.5,1,3.4,1.6,5.2,1.6l1.8,0
                                c1.3,0,2.6-0.3,3.8-0.8c0.5-0.2,1-0.5,1.4-0.8v18.7c0,1.4,1.2,2.6,2.6,2.6h8.1c1.4,0,2.6-1.2,2.6-2.6V385c1.4-0.4,2.4-1.7,2.4-3.2
                                v-7.4C-276.1,371.9-277.1,369.7-278.8,367.9z M-285.2,355.9c2.6,0,4.7,2.1,4.7,4.7c0,2.6-2.1,4.7-4.7,4.7s-4.7-2.1-4.7-4.7
                                C-289.9,358-287.8,355.9-285.2,355.9z M-315.5,360.6c0-2.6,2.1-4.7,4.7-4.7c2.6,0,4.7,2.1,4.7,4.7c0,2.6-2.1,4.7-4.7,4.7
                                C-313.4,365.3-315.5,363.2-315.5,360.6z M-298.9,376.4c-2.1,0-4.1-0.9-5.5-2.4c-0.2-0.3-0.6-0.4-0.9-0.3c-0.5,0.1-0.8,0.5-0.8,1
                                v20.9c0,0.4-0.3,0.7-0.7,0.7h-2.5c-0.3,0-0.6-0.3-0.6-0.6v-9.8c0-0.5-0.4-1-0.9-1c-0.6,0-1.1,0.4-1.1,1v9.8c0,0.3-0.3,0.6-0.6,0.6
                                h-2.5c-0.4,0-0.7-0.3-0.7-0.7v-11.3c0-0.5-0.4-1-1-1c-0.8,0-1.4-0.6-1.4-1.4v-7.3c0-3.9,3.2-7.2,7.2-7.2c3.1,0,6.1,1.7,7.6,4.5
                                c0.7,1.3,2.1,2,3.6,2h1.4c0.7,0,1.3,0.5,1.3,1.2c0.1,0.8-0.5,1.4-1.3,1.4L-298.9,376.4L-298.9,376.4z M-278.1,381.8
                                c0,0.8-0.6,1.4-1.4,1.4c-0.5,0-1,0.4-1,1v11.3c0,0.4-0.3,0.7-0.7,0.7h-2.5c-0.3,0-0.6-0.3-0.6-0.6v-9.8c0-0.5-0.4-1-0.9-1
                                c-0.6,0-1.1,0.4-1.1,1v9.8c0,0.3-0.3,0.6-0.6,0.6h-2.5c-0.4,0-0.7-0.3-0.7-0.7v-20.9c0-0.4-0.2-0.7-0.5-0.9
                                c-0.4-0.2-0.9-0.1-1.2,0.2c-0.7,0.8-1.5,1.4-2.5,1.8c-0.3,0.1-0.6,0.3-1,0.3c0.1-0.3,0.2-0.7,0.2-1c0-0.5-0.1-1-0.4-1.5
                                c1-0.3,1.9-1,2.4-1.9c1.6-2.7,4.5-4.4,7.7-4.4c1.9,0,3.7,0.7,5,2.1c1.3,1.3,2.1,3.1,2.1,5L-278.1,381.8L-278.1,381.8z"
                            />
                        </svg>
                    </div>
                    <div>{{ $t('global.UserAgreementTitle') }}</div>
                </div>
            </a>
            <a class="group button-transparent h-full bg-surface border border-transparent hover:!bg-surface hover:border-primary-dark/75 hover:!text-primary-dark active:!text-white" :href="route('data-processing-rules')">
                <div class="py-2.5 pl-3.5 pr-2 flex items-center justify-start h-full w-full bg-gradient-to-br hover:from-primary-light/10 hover:to-primary-light/30 group-active:bg-transparent transition-all">
                    <div class="flex items-center justify-center">
                        <svg
                            class="h-7 w-7 mb-0.5 mr-2"
                            xmlns:xlink="http://www.w3.org/1999/xlink"
                            version="1.1"
                            baseProfile="full"
                            viewBox="0 0 75 75"
                            xml:space="preserve"
                        >
                            <path
                                d="M38,23.9l0.8,0.6l1.5,1.3l0.1,0.1l0.1,0.1l1.8,1.4l0.1,0.1l0.1,0.1c0.9,0.6,1.9,1.2,3,1.6c1.2,0.5,2.6,0.7,4.1,0.7
                                c1,0,2.1-0.1,3.3-0.4c0.5-0.1,1.1-0.3,1.7-0.5c0,1.6-0.2,3-0.3,4.4c-0.2,1.8-0.6,3.6-1,5.2c-0.4,1.6-0.9,3-1.5,4.3
                                c-0.6,1.3-1.2,2.5-1.9,3.6c-0.7,1.1-1.4,2.1-2.1,2.9c-0.7,0.9-1.5,1.7-2.2,2.4c-2.2,2.1-4.8,3.8-7.5,4.9c-2.8-1.1-5.3-2.8-7.5-4.9
                                c-1.1-1.1-2.2-2.4-3.3-3.8c-1.1-1.4-2-3.2-2.9-5.1c-0.9-2-1.6-4.3-2.1-6.8c-0.4-2.1-0.7-4.5-0.8-7.1c0.6,0.2,1.2,0.4,1.7,0.5
                                c1.2,0.3,2.3,0.4,3.3,0.4c1.5,0,2.8-0.2,4.1-0.7c1.1-0.4,2.1-0.9,3-1.6l0.1-0.1l0.1-0.1l1.8-1.4l0.1-0.1l0.1-0.1l1.4-1.3L38,23.9
                                M38,19.8c-0.6,0-1.2,0.1-1.7,0.4l-1.6,1.1l-1.6,1.4l-1.8,1.4c-0.7,0.4-1.4,0.8-2.2,1.1c-0.8,0.3-1.7,0.4-2.7,0.4
                                c-0.7,0-1.5-0.1-2.4-0.3c-0.9-0.2-1.8-0.5-2.9-1c-0.5-0.2-0.9-0.3-1.3-0.3c-0.4,0-0.8,0.1-1.1,0.3c-0.3,0.2-0.6,0.4-0.8,0.7l-0.5,1
                                l-0.2,1.1c0,3.5,0.3,6.7,0.9,9.5c0.6,2.8,1.4,5.4,2.3,7.6c1,2.2,2.1,4.2,3.3,5.9c1.2,1.7,2.5,3.1,3.8,4.4c3,2.9,6.5,5,10.3,6.3
                                c3.8-1.3,7.3-3.4,10.3-6.3c0.9-0.8,1.7-1.7,2.6-2.8c0.8-1,1.6-2.2,2.4-3.4c0.8-1.2,1.5-2.6,2.1-4.1c0.7-1.5,1.2-3.1,1.7-4.9
                                c0.5-1.8,0.9-3.7,1.1-5.7c0.3-2,0.4-4.2,0.4-6.6l-0.1-1.1l-0.4-1c-0.2-0.3-0.4-0.5-0.7-0.7c-0.3-0.2-0.6-0.3-1-0.3
                                c-0.4,0-0.9,0.1-1.4,0.3c-1,0.5-2,0.8-2.8,1c-0.9,0.2-1.7,0.3-2.4,0.3c-1,0-1.9-0.1-2.7-0.4c-0.8-0.3-1.5-0.7-2.2-1.1l-1.8-1.4
                                l-1.6-1.4l-1.6-1.1C39.2,19.9,38.6,19.8,38,19.8L38,19.8z"
                            />
                        </svg>
                    </div>
                    <div>{{ $t('global.PrivacyPolicyTitle') }}</div>
                </div>
            </a>
        </div>


        <h2 class="text-lg text-neutral-600 pl-11 mt-10 mb-2">{{ $t('help.HelpContactsHeader') }}:</h2>

        <div class="flex flex-col md:flex-row md:space-x-2 space-y-1 md:space-y-0">
            <a class="w-full group button-transparent h-full bg-surface border border-transparent hover:!bg-surface hover:border-primary-dark/75 hover:!text-primary-dark active:!text-white" href="mailto:hd@medicloud.lv">
                <div class="py-2.5 pl-3.5 pr-2 flex items-center justify-start h-full w-full bg-gradient-to-br hover:from-primary-light/10 hover:to-primary-light/30 group-active:bg-transparent transition-all">
                    <div class="flex items-center justify-center">
                        <svg viewBox="0 0 24 24" class="h-6 w-6 mr-2"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" /><path d="M0 0h24v24H0z" fill="none" /></svg>
                    </div>
                    <div>{{ $t('global.Email') }}</div>
                </div>
            </a>

            <a class="w-full group button-transparent h-full bg-surface border border-transparent hover:!bg-surface hover:border-primary-dark/75 hover:!text-primary-dark active:!text-white" href="http://m.me/eVeselibasPunkts.lv">
                <div class="py-2.5 pl-3.5 pr-2 flex items-center justify-start h-full w-full bg-gradient-to-br hover:from-primary-light/10 hover:to-primary-light/30 group-active:bg-transparent transition-all">
                    <div class="flex items-center justify-center">
                        <svg
                            class="h-6 w-6 mr-2"
                            version="1.1"
                            xmlns:xlink="http://www.w3.org/1999/xlink"
                            x="0px"
                            y="0px"
                            viewBox="0 0 1024 1024"
                            xml:space="preserve"
                        ><path d="M512,11C232,11,15,216.1,15,493.1C15,638,74.4,763.2,171.1,849.7c8.1,7.3,13,17.4,13.4,28.3l2.7,88.4 c0.9,28.2,30,46.5,55.8,35.2l98.6-43.5c8.4-3.7,17.7-4.4,26.5-2c45.3,12.5,93.6,19.1,143.9,19.1c280,0,497-205.1,497-482.1 S792,11,512,11z M810.4,382l-146,231.6c-23.2,36.8-73,46-107.8,19.9l-116.1-87.1c-10.7-8-25.3-7.9-35.9,0.1l-156.8,119 c-20.9,15.9-48.3-9.2-34.2-31.4l146-231.6c23.2-36.8,73-46,107.8-19.9l116.1,87.1c10.7,8,25.3,7.9,35.9-0.1l156.8-119 C797.1,334.7,824.5,359.8,810.4,382z" /></svg>
                    </div>
                    <div>{{ $t('global.Messenger') }}</div>
                </div>
            </a>
        </div>

        <div class="mt-2 text-right text-neutral-600 ">{{ $t('global.Version') }}: {{ evp.appVersion }}</div>
    </evp-layout>
</template>

<script setup lang="ts">
    import { onMounted, ref } from 'vue'
    import EvpLayout from '@/components/EvpLayout.vue'
    import { route } from 'ziggy'
    import NavigationHeader from '@/components/NavigationHeader.vue'

    interface HelpArticle {
        Id: number
        TitleLV: string
        TitleEN: string
        TitleRU: string
        Category: number
        ArticleUri: string
    }

    interface HelpCategory {
        Id: number
        CategoryTitleLV: string
        CategoryTitleEN: string
        CategoryTitleRU: string
        articles: HelpArticle[]
    }

    const evp = window.evp
    const langCapitalized = window.evp.currentLocale.toUpperCase()
    const categories: HelpCategory[] = (window as any).helpListData.categories as HelpCategory[]

    // Raksts, kuram jāpievērš lietotāja uzmanība
    const activeArticleSlug = ref<string | null>(null)
    const highlightCategory = ref(false)

    onMounted(() => {
        const urlParams = new URLSearchParams(window.location.search)
        activeArticleSlug.value = urlParams.get('activeArticle')
        highlightCategory.value = urlParams.get('highlightCategory') === '1'

        const activeArticleElement: HTMLElement | null = document.querySelector(`[data-article-slug="${activeArticleSlug.value}"]`) ?? null
    
        if (activeArticleElement) {
            window.scrollTo({
                top: highlightCategory.value 
                    ? (activeArticleElement.parentElement?.parentElement as HTMLElement).offsetTop - 100 
                    : activeArticleElement.offsetTop - 100,
                behavior: 'smooth'
            })
        }
    })

</script>